// Data service for loading dashboard data from backend API
// Replaces static imports with dynamic API calls

import authService from './authService';

const API_CONFIG = {
  baseUrl: process.env.REACT_APP_API_BASE_URL || '',
  endpoints: {
    getData: '/api/admin/data',
    uploadData: '/api/admin/data/upload',
    validateData: '/api/admin/data/validate',
    downloadData: '/api/admin/data/download',
    listBackups: '/api/admin/data/backups'
  }
};

class DataService {
  constructor() {
    this.cache = {
      data: null,
      timestamp: null,
      expiry: 5 * 60 * 1000 // 5 minutes cache
    };
  }

  // Get dashboard data with caching
  async getDashboardData(forceRefresh = false) {
    try {
      // Check cache first
      if (!forceRefresh && this.cache.data && this.cache.timestamp) {
        const now = Date.now();
        if (now - this.cache.timestamp < this.cache.expiry) {
          return this.cache.data;
        }
      }

      
      const url = `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.getData}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch dashboard data');
      }

      // Process the data
      const processedData = this.processRawData(result.data);

      // Update cache
      this.cache = {
        data: processedData,
        timestamp: Date.now(),
        expiry: this.cache.expiry
      };

      return processedData;

    } catch (error) {
      console.error('❌ Error loading dashboard data:', error);
      
      // Return fallback data if API fails
      return this.getFallbackData();
    }
  }

  // Process raw data to match frontend expectations
  processRawData(rawData) {
    const { categories, apps, analytics, configuration } = rawData;

    // Create category lookup map
    const categoryMap = {};
    categories.forEach(cat => {
      categoryMap[cat.id] = cat;
    });

    // Process apps to include category objects
    const processedApps = apps.map(app => ({
      ...app,
      category: categoryMap[app.categoryId] || {
        id: 'unknown',
        name: 'Unknown Category',
        color: '#6B7280',
        icon: '❓'
      }
    }));

    return {
      categories,
      apps: processedApps,
      analytics,
      configuration,
      metadata: rawData.metadata
    };
  }

  // Upload new dashboard data
  async uploadDashboardData(file) {
    try {

      const formData = new FormData();
      formData.append('dataFile', file);

      const url = `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.uploadData}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
        body: formData
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || `Upload failed: ${response.status}`);
      }

      if (!result.success) {
        throw new Error(result.error || 'Upload failed');
      }

      // Clear cache to force refresh
      this.clearCache();

      return result;

    } catch (error) {
      console.error('❌ Error uploading dashboard data:', error);
      throw error;
    }
  }

  // Validate dashboard data
  async validateDashboardData(data) {
    try {

      const url = `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.validateData}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authService.getAuthHeaders()
        },
        body: JSON.stringify(data)
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || `Validation failed: ${response.status}`);
      }

      return result;

    } catch (error) {
      console.error('❌ Error validating dashboard data:', error);
      throw error;
    }
  }

  // Download current dashboard data
  async downloadDashboardData() {
    try {
      const url = `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.downloadData}`;
      const response = await fetch(url, {
        headers: authService.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status}`);
      }

      // Create blob and download
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = 'dashboard_data.json';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

    } catch (error) {
      console.error('❌ Error downloading dashboard data:', error);
      throw error;
    }
  }

  // Get list of backups
  async listBackups() {
    try {
      const url = `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.listBackups}`;
      const response = await fetch(url, {
        headers: authService.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to list backups: ${response.status}`);
      }

      const result = await response.json();
      return result.success ? result.backups : [];

    } catch (error) {
      console.error('❌ Error listing backups:', error);
      return [];
    }
  }

  // Clear cache
  clearCache() {
    this.cache = {
      data: null,
      timestamp: null,
      expiry: this.cache.expiry
    };
  }

  // Fallback data if API fails
  getFallbackData() {
    
    return {
      categories: [
        {
          id: 'document-processing',
          name: 'Document Processing',
          color: '#3B82F6',
          icon: '📄'
        },
        {
          id: 'development-devops',
          name: 'Development & DevOps',
          color: '#10B981',
          icon: '⚙️'
        }
      ],
      apps: [
        {
          id: 'fallback-app',
          name: 'Fallback App',
          url: '#',
          description: 'Fallback data when API is unavailable',
          category: {
            id: 'document-processing',
            name: 'Document Processing',
            color: '#3B82F6',
            icon: '📄'
          },
          tags: ['Fallback'],
          status: 'Available',
          deployed: true
        }
      ],
      analytics: {
        dashboardStats: {
          totalApps: 1,
          activeUsers: 0,
          requestsToday: 0
        },
        popularApps: [],
        categoryUsage: [],
        monthlyTrends: []
      },
      configuration: {
        theme: {
          primaryColor: '#3B82F6',
          secondaryColor: '#10B981'
        },
        features: {
          enableAnalytics: true,
          enableRequestSystem: true
        }
      }
    };
  }
}

// Singleton instance
export const dataService = new DataService();

// Convenience functions
export const getDashboardData = (forceRefresh = false) => {
  return dataService.getDashboardData(forceRefresh);
};

export const uploadDashboardData = (file) => {
  return dataService.uploadDashboardData(file);
};

export const validateDashboardData = (data) => {
  return dataService.validateDashboardData(data);
};

export const downloadDashboardData = () => {
  return dataService.downloadDashboardData();
};

export const listBackups = () => {
  return dataService.listBackups();
};

export const clearDataCache = () => {
  dataService.clearCache();
};

export default dataService;